import {  html, nothing } from "lit";
import type { LatencyPoint } from "../definitions/LatencyPoint";
import { calculateAverage, calculatePercentageBelow } from "../stats";
import { formatRollingYearRange } from "../dateRange";
import { LmaxBaseChartComponent } from "../../shared/components/lmax-base-chart-component";

export interface LegendStats {
  data: LatencyPoint[];
}

const PLACEHOLDER = /\$\{(avg|tradesExecuted|range)\}/;

function parseThreshold(expr: string): number | null {
  const match = expr.match(/[\d.]+/);
  return match ? Number(match[0]) : null;
}

export class LmaxChartLegend extends LmaxBaseChartComponent {
  static properties = {
    lableText: { type: String, attribute: "labletext" },
    tradesExecutedIn: { type: String, attribute: "tradesexecutedin" },
    stats: { attribute: false },
    styleHref: { type: String, attribute: "stylehref" },
  };

  declare lableText: string;
  declare tradesExecutedIn: string;
  declare stats: LegendStats | null;
  declare styleHref: string;


  constructor() {
    super();
    this.lableText = "";
    this.tradesExecutedIn = "";
    this.stats = null;
    this.styleHref = "";
  }
 
  private resolve(): { title: string; value: string } {
    const match = this.lableText.match(PLACEHOLDER);
    if (!match) return { title: this.lableText, value: "" };

    const title = this.lableText.slice(0, match.index).trim();
    // Date-derived, not data-derived — resolves from the clock regardless of
    // whether the chart's stats have loaded yet.
    if (match[1] === "range") {
      return { title, value: formatRollingYearRange() };
    }
    if (!this.stats) return { title, value: "—" };

    const { data } = this.stats;
    if (match[1] === "avg") {
      return { title, value: `${calculateAverage(data).toFixed(2)}ms` };
    }

    const threshold = parseThreshold(this.tradesExecutedIn);
    const value =
      threshold === null ? "—" : `${calculatePercentageBelow(data, threshold).toFixed(2)}%`;
    return { title, value };
  }

  render() {
    if (!this.lableText) return nothing;
    const { title, value } = this.resolve();
    return html`
      <link rel="stylesheet" href=${this.styleHref} />
      ${this.cssIncludesResolved}
      <div class="stat-card">
        <div class="stat-title">${title}</div>
        <div class="stat-value">${value}</div>
      </div>
    `;
  }
}

customElements.define("lmax-chart-legend", LmaxChartLegend);

declare global {
  interface HTMLElementTagNameMap {
    "lmax-chart-legend": LmaxChartLegend;
  }
}
